import React, { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { Spinner } from "./ui/kit";
import NahratObrazek from "./NahratObrazek";
import api, { apiError } from "../lib/api";

// Stejná pole jako strana „Hodnocená budova“ v PDF (backend pdf/data.py BUDOVA_POLE).
const POLE = [
  ["typ", "Typ objektu", "", "Rodinný dům, novostavba"],
  ["zdroj_tepla", "Současný zdroj tepla", "", "Plynový kotel"],
  ["priprava_tuv", "Současná příprava teplé vody", "", "Elektrický bojler"],
  ["vnitrni_teplota", "Vnitřní teplota", "°C", "22"],
  ["tepelna_ztrata", "Tepelná ztráta", "kW", "6,5"],
  ["spotreba", "Spotřeba na vytápění", "kWh/rok", "12 000"],
  ["plocha", "Podlahová plocha", "m²", "180"],
  ["osob", "Počet osob v domácnosti", "", "4"],
];
const prazdna = () => Object.fromEntries(POLE.map(([k]) => [k, ""]).concat([["poznamka", ""]]));

/**
 * Záložka Budova v obchodním případu: údaje a fotka domu pro stranu
 * „Hodnocená budova“ v PDF nabídky i souhrnu. Vyplní obchodník i technik při
 * zaměření; co zůstane prázdné, se v PDF nevypíše.
 */
export default function BudovaPripadu({ pripadId, budova, muzeUpravit, onUlozeno }) {
  const [uprava, setUprava] = useState(false);
  const [f, setF] = useState(prazdna);
  const [foto, setFoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const b = budova || {};

  useEffect(() => {
    if (!b.ma_foto) { setFoto(null); return; }
    api.get(`/api/obchodni-pripady/${pripadId}/budova/foto`).then(({ data }) => setFoto(data.obrazek)).catch(() => {});
  }, [pripadId, b.ma_foto]);

  const zacit = () => { setF({ ...prazdna(), ...Object.fromEntries(Object.entries(b).filter(([k]) => k !== "ma_foto")) }); setUprava(true); setChyba(""); };
  const ulozit = async () => {
    setBusy(true); setChyba("");
    try {
      const { data } = await api.put(`/api/obchodni-pripady/${pripadId}/budova`, f);
      onUlozeno?.(data); setUprava(false);
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const ulozFoto = async (v) => {
    setChyba("");
    try {
      const { data } = await api.put(`/api/obchodni-pripady/${pripadId}/budova/foto`, { obrazek: v || "" });
      setFoto(v || null); onUlozeno?.({ ...b, ma_foto: data.ma_foto });
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  };

  const vyplnene = POLE.filter(([k]) => b[k]);
  return (
    <div className="rise px-4 md:px-5 py-4" data-testid="pripad-budova">
      <p className="t-meta text-muted mb-3">Údaje se ukážou v PDF nabídky a souhrnu případu na straně „Hodnocená budova“. Prázdné se nevypíše.</p>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
        <div className="min-w-0">
          {muzeUpravit ? <NahratObrazek tvar="budova" value={foto} onChange={ulozFoto} testid="budova-foto" />
            : foto ? <img src={foto} alt="Budova" className="w-full rounded-[14px] object-cover aspect-[3/2]" />
              : <div className="aspect-[3/2] rounded-[14px] bg-surface flex items-center justify-center t-meta text-muted">bez fotky</div>}
        </div>
        {uprava ? (
          <div className="min-w-0 flex flex-col gap-3" data-testid="budova-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {POLE.map(([k, popisek, jednotka, priklad]) => (
                <label key={k} className="min-w-0">
                  <span className="label-up text-muted block mb-1">{popisek}{jednotka && ` (${jednotka})`}</span>
                  <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={priklad} maxLength={80}
                    className="field w-full min-w-0" data-testid={`budova-${k}`} />
                </label>
              ))}
            </div>
            <label>
              <span className="label-up text-muted block mb-1">Poznámka k budově</span>
              <textarea value={f.poznamka || ""} onChange={(e) => setF({ ...f, poznamka: e.target.value })} maxLength={300} rows={2}
                className="field w-full min-w-0 py-2 h-auto resize-y" data-testid="budova-poznamka" />
            </label>
            {chyba && <p className="t-meta text-danger">{chyba}</p>}
            <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
              <button type="button" onClick={() => setUprava(false)} className="tap md:min-h-0 h-10 px-4 rounded-full t-meta md:text-[13px] font-700 text-sec hover:text-ink">Zrušit</button>
              <button type="button" onClick={ulozit} disabled={busy} data-testid="budova-ulozit"
                className="tap md:min-h-0 w-full md:w-auto h-11 md:h-10 px-5 rounded-full bg-accent text-white text-[14px] md:text-[13px] font-700 inline-flex items-center justify-center disabled:opacity-60">
                {busy ? <Spinner /> : "Uložit"}
              </button>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            {vyplnene.length || b.poznamka ? (
              <dl className="rounded-[14px] bg-surface px-3.5 py-1" data-testid="budova-udaje">
                {vyplnene.map(([k, popisek, jednotka]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 py-2 border-b border-line-soft last:border-0">
                    <dt className="t-meta text-muted">{popisek}</dt>
                    <dd className="t-telo font-600 text-ink text-right">{b[k]}{jednotka && !String(b[k]).endsWith(jednotka) ? ` ${jednotka}` : ""}</dd>
                  </div>
                ))}
                {b.poznamka && <p className="t-meta text-sec py-2">{b.poznamka}</p>}
              </dl>
            ) : <p className="t-meta text-muted py-2">Zatím nevyplněno.</p>}
            {chyba && <p className="t-meta text-danger mt-2">{chyba}</p>}
            {muzeUpravit && (
              <button type="button" onClick={zacit} data-testid="budova-upravit"
                className="tap md:min-h-0 mt-2 inline-flex items-center gap-1.5 t-meta font-700 text-accent">
                <Pencil size={14} /> {vyplnene.length ? "Upravit údaje" : "Vyplnit údaje o budově"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
